// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Notifications Dropdown
// Notification panel with real-time alerts and messages
// ============================================================================

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Bell,
  AlertTriangle,
  CheckCircle,
  Clock,
  Package,
  X,
  Settings,
  MailOpen,
  Trash2,
} from 'lucide-react';
import { clsx } from 'clsx';

interface Notification {
  id: string;
  type: 'urgent' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

// Mock notifications for demo
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    type: 'urgent',
    title: 'STAT Order Pending',
    message: 'RX53136628754 for John Doe requires immediate attention',
    timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 min ago
    read: false,
    actionUrl: '/queue/ready',
  },
  {
    id: '2',
    type: 'warning',
    title: 'DUR Alert Requires Review',
    message: 'Drug interaction detected for RX20260004',
    timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 min ago
    read: false,
    actionUrl: '/queue/dur',
  },
  {
    id: '3',
    type: 'info',
    title: 'Inventory Low',
    message: 'Lisinopril 10mg stock below reorder point (45 units remaining)',
    timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
    read: false,
  },
  {
    id: '4',
    type: 'success',
    title: 'Batch Processing Complete',
    message: '127 prescriptions processed successfully',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    read: true,
  },
  {
    id: '5',
    type: 'info',
    title: 'System Update',
    message: 'New features available - check Settings for details',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 hours ago
    read: true,
  },
];

const typeConfig = {
  urgent: {
    icon: AlertTriangle,
    bgColor: 'bg-red-50',
    iconColor: 'text-red-500',
    borderColor: 'border-l-red-500',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-500',
    borderColor: 'border-l-amber-500',
  },
  info: {
    icon: Package,
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-500',
    borderColor: 'border-l-blue-500',
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-50',
    iconColor: 'text-green-500',
    borderColor: 'border-l-green-500',
  },
};

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationsDropdown({ isOpen, onClose }: NotificationsDropdownProps) {
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Dropdown */}
      <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-slate-600" />
            <span className="font-semibold text-slate-900">Notifications</span>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                title="Mark all as read"
              >
                <MailOpen className="h-4 w-4 text-slate-500" />
              </button>
            )}
            <button
              onClick={clearAll}
              className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
              title="Clear all"
            >
              <Trash2 className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Bell className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No notifications</p>
              <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => {
                const config = typeConfig[notification.type];
                const Icon = config.icon;

                return (
                  <div
                    key={notification.id}
                    className={clsx(
                      'relative px-4 py-3 hover:bg-slate-50 transition-colors border-l-4',
                      config.borderColor,
                      !notification.read && 'bg-slate-50/50'
                    )}
                  >
                    <div className="flex gap-3">
                      <div
                        className={clsx(
                          'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
                          config.bgColor
                        )}
                      >
                        <Icon className={clsx('h-4 w-4', config.iconColor)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={clsx(
                              'text-sm font-medium truncate',
                              notification.read ? 'text-slate-600' : 'text-slate-900'
                            )}
                          >
                            {notification.title}
                          </p>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 rounded hover:bg-slate-200 transition-colors"
                          >
                            <X className="h-3 w-3 text-slate-400" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                          </span>
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="text-xs text-pharmacy-primary hover:underline"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="absolute top-3 right-12 w-2 h-2 rounded-full bg-pharmacy-primary" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
          <button className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
            <Settings className="h-4 w-4" />
            Notification Settings
          </button>
        </div>
      </div>
    </>
  );
}



